'use client';

import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { SolidNavbar } from '@/components/SolidNavbar';
import { Footer } from '@/components/Footer';
import { CatalogSidebar } from '@/components/catalog/CatalogSidebar';
import { ModuleCard } from '@/components/catalog/ModuleCard';
import { CatalogRightPanel } from '@/components/catalog/CatalogRightPanel';
import { CatalogBannerCarousel } from '@/components/CatalogBannerCarousel';
import { CatalogSearchHeader } from '@/components/search/CatalogSearchHeader';
import { BundleCard } from '@/components/catalog/BundleCard';
import { testModuleApi, catalogApi, bundleApi } from '@/lib/api';
import { CatalogEmptyState } from '@/components/catalog/CatalogEmptyState';
import type { TestModule, ExamType, Bundle } from '@/lib/api';
import { useOwnedItems } from '@/context/OwnedItemsContext';
import { usePreferences } from '@/context/PreferencesContext';
import { filterByExamType, filterBySegment, sortByInterest } from '@/lib/catalogPersonalization';
import { searchItems } from '@/lib/search';
import { useUrlQuery } from '@/components/search/useUrlQuery';
import { useSegmentPolicy } from '@/hooks/useSegmentPolicy';
import { SlidersHorizontal, ChevronLeft, ChevronRight, ChevronDown, Layers } from 'lucide-react';

import { PAGE_SIZE } from './constants';
import { PAGE_CONTAINER } from '@/components/ui';

type SortOption = 'newest' | 'price_asc' | 'price_desc' | 'discount';

function moduleEffectivePrice(m: TestModule) {
  return m.promoPrice != null ? m.promoPrice : m.price;
}

export function ModuleSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
      <div className="h-4 bg-slate-200 rounded w-1/3 mb-3" />
      <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-slate-200 rounded w-full mb-1" />
      <div className="h-4 bg-slate-200 rounded w-2/3 mb-4" />
      <div className="h-8 bg-slate-200 rounded" />
    </div>
  );
}

function ModuleCatalogContent({
  initialModules = [],
  initialBundles = [],
  initialExamTypes = [],
}: {
  initialModules?: TestModule[];
  initialBundles?: Bundle[];
  initialExamTypes?: ExamType[];
}) {
  const t = useTranslations('testsCatalog');
  const tBundles = useTranslations('bundlesSection');
  const tCatalog = useTranslations('catalog');
  const searchParams = useSearchParams();
  const examTypeSlugInit = searchParams.get('examTypeSlug') ?? undefined;
  const isFreeInit = searchParams.get('isFree') ?? undefined;

  // Seed from the server-rendered list so hydration doesn't flash
  // content -> skeletons -> content. Only valid with no filter active: the server always
  // renders the unfiltered catalog, so a filtered landing (/tests?examTypeSlug=cpns) must
  // fetch rather than briefly show the wrong modules.
  const canSeed = !examTypeSlugInit && !isFreeInit && initialModules.length > 0;

  // Bundles have no filter/URL param dependence (the section is hidden, not refetched, when an
  // exam family tile is active — see displayedBundles below), so seeding only needs to check that
  // the server actually returned something. When it did, CatalogShell already rendered this exact
  // list server-side, so skipping the client fetch entirely keeps the hydrated DOM identical to
  // the SSR HTML (no skeleton, no swap — this is what fixed the CLS the old client-only fetch caused).
  const canSeedBundles = initialBundles.length > 0;

  const [modules, setModules] = useState<TestModule[]>(canSeed ? initialModules : []);
  // Seeded so the exam family tiles match the server-rendered shell.
  const [examTypes, setExamTypes] = useState<ExamType[]>(initialExamTypes);
  const [bundles, setBundles] = useState<Bundle[]>(canSeedBundles ? initialBundles : []);
  const [loading, setLoading] = useState(!canSeed);
  const skipInitialFetch = useRef(canSeed);
  const skipInitialBundleFetch = useRef(canSeedBundles);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [page, setPage] = useState(1);

  const router = useRouter();
  const pathname = usePathname();
  const { ownedModuleIds, ownedBundleIds } = useOwnedItems();
  const { segment, interests } = usePreferences();

  const [showAllBundles, setShowAllBundles] = useState(false);
  const examTypeSlug = searchParams.get('examTypeSlug') ?? undefined;
  const isFree = searchParams.get('isFree') ?? undefined;
  const hasDiscount = searchParams.get('hasDiscount') === 'true';
  const duration = searchParams.get('duration') ?? '';
  const sort = (searchParams.get('sort') ?? 'newest') as SortOption;
  const { q, input: qInput, onChange: handleQueryChange } = useUrlQuery();

  useEffect(() => {
    catalogApi.getExamTypes().then(setExamTypes).catch(() => {});
  }, []);

  useEffect(() => {
    // Seeded by the server: skip the fetch (see canSeedBundles above). If the server came back
    // empty — genuinely no active bundles, or a transient SSR-side failure — retry once
    // client-side through the proxy with no loading UI, so a real "no bundles" stays that way
    // (showBundlesSection below only flips on when displayedBundles actually has items) and a
    // recovered fetch just pops the section in rather than swapping a skeleton for content.
    if (skipInitialBundleFetch.current) {
      skipInitialBundleFetch.current = false;
      return;
    }
    bundleApi.getPublishedBundles()
      .then(setBundles)
      .catch(() => setBundles([]));
  }, []);

  // Reset back to the personalized view whenever the segment itself changes
  // (matches the previous behavior of the inline fetch effect).
  useEffect(() => {
    setShowAllBundles(false);
  }, [segment]);

  // Segment de-noising data for the bundles section — shared/cached across
  // every consumer of the segment-policy endpoint on the page via
  // useSegmentPolicy (same source Catalog.tsx and PersonalizationStrip use).
  // null = no segment or fetch failed, both fall back to the full bundle
  // list. See docs/personalization-spec.md — step 8, bundle "any of many" rule.
  const { examTypes: segmentExamTypes, status: policyStatus } = useSegmentPolicy(segment);
  const policyPending = !!segment && policyStatus === 'loading';
  const visibleSlugs = useMemo(() => {
    if (!segment || policyStatus !== 'ready') return null;
    return new Set(segmentExamTypes.map((et) => et.slug));
  }, [segment, policyStatus, segmentExamTypes]);

  useEffect(() => {
    // First run with server-seeded data and no filter: the list is already correct, so
    // skip the refetch that would otherwise blank it back to skeletons.
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    setLoading(true);
    testModuleApi
      .getPublishedModules({ examTypeSlug, isFree })
      .then(setModules)
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
    setPage(1);
  }, [examTypeSlug, isFree]);

  // Bundle de-noise + sort. A bundle is hidden only if NONE of its exam
  // types are in the segment's visible set (kept if ANY is), and sorted
  // toward the top if ANY exam type matches the interest slug — bundles are
  // cross-cutting, unlike single-exam modules/courses. filterBySegment /
  // sortByInterest already operate over the full examTypes array via
  // `.some()`, so the "any of many" rule holds without modification.
  // An active exam family tile overrides the segment: the visitor asked for that
  // exam explicitly, so its bundles must show even if the segment would hide them.
  const displayedBundles = useMemo(() => {
    const visible = examTypeSlug
      ? filterByExamType(bundles, examTypeSlug)
      : filterBySegment(bundles, visibleSlugs, showAllBundles);
    return sortByInterest(visible, interests, (b) => ownedBundleIds.has(b.id));
  }, [bundles, examTypeSlug, visibleSlugs, showAllBundles, interests, ownedBundleIds]);

  // Segment personalization only applies to the unfiltered ("Semua") view.
  const isBundlePersonalized = !examTypeSlug && !!segment;

  // Personalization hid every bundle even though bundles exist — offer the
  // escape hatch instead of a silent empty section.
  const bundlesPersonalizedEmpty =
    bundles.length > 0 && displayedBundles.length === 0 && isBundlePersonalized && !showAllBundles;

  // With a tile active, a filter that matches no bundle hides the section like
  // "no bundles at all" does, instead of showing unrelated exams' bundles.
  const showBundlesSection = examTypeSlug ? displayedBundles.length > 0 : bundles.length > 0;

  const hasFilters = !!(q || examTypeSlug || isFree || hasDiscount || duration || sort !== 'newest');
  // Panel-controlled filters only (Tipe / Penawaran / Durasi) — exam type has its own
  // visible state (the tiles under the title), so it isn't double-counted here.
  const activeFilterCount = (isFree ? 1 : 0) + (hasDiscount ? 1 : 0) + (duration ? 1 : 0);

  const updateParam = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value); else params.delete(key);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // Same param-merge logic as updateParam, but returns a real href instead of
  // navigating — used for the exam family tiles so those filters stay crawlable
  // <Link>s (real URLs, middle-click/cmd-click, copy-link) instead of onClick-only
  // buttons, while still preserving every other active search param.
  const buildHref = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) params.set(key, value); else params.delete(key);
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  // Search runs over the full fetched list, before pagination, and a new query starts at page 1.
  useEffect(() => { setPage(1); }, [q]);

  const searched = useMemo(
    () => searchItems(modules, q, (m) => ({
      title: m.title,
      text: m.description,
      families: m.examTypes.map((et) => et.name),
    })),
    [modules, q],
  );

  const sorted = [...searched]
    .filter(m => {
      if (hasDiscount && !(m.promoPrice != null && m.promoPrice < m.basePrice)) return false;
      if (duration) {
        const total = m.tests?.reduce((s, t) => s + (t.durationMins ?? 0), 0) ?? 0;
        if (duration === 'short' && total >= 120) return false;
        if (duration === 'medium' && (total < 120 || total > 300)) return false;
        if (duration === 'long' && total <= 300) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aOwned = ownedModuleIds.has(a.id) ? 1 : 0;
      const bOwned = ownedModuleIds.has(b.id) ? 1 : 0;
      if (aOwned !== bOwned) return aOwned - bOwned;
      switch (sort) {
        case 'price_asc': return moduleEffectivePrice(a) - moduleEffectivePrice(b);
        case 'price_desc': return moduleEffectivePrice(b) - moduleEffectivePrice(a);
        case 'discount': {
          const ad = a.promoPrice != null ? (a.basePrice - a.promoPrice) / a.basePrice : 0;
          const bd = b.promoPrice != null ? (b.basePrice - b.promoPrice) / b.basePrice : 0;
          return bd - ad;
        }
        // With a query, keep the relevance order from searchItems (the sort is stable).
        default: return q ? 0 : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <SolidNavbar breadcrumbs={[{ label: t('breadcrumb'), href: '/tests' }]} />
      <main className="bg-slate-50 pb-12">
        {/* Hero. Heading, search box and exam family tiles: the count and the active-filter recap that used to
            live here are both already shown by the toolbar and the "Menampilkan X–Y dari N"
            line above the grid. The h1 stays for SEO/a11y. */}
        <div className="bg-pattern bg-teal-700 py-8 sm:py-10 text-white">
          <div className={PAGE_CONTAINER}>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">{t('title')}</h1>
            <CatalogSearchHeader
              query={qInput}
              onQueryChange={handleQueryChange}
              examTypes={examTypes}
              activeSlug={examTypeSlug}
              hrefFor={(slug) => buildHref('examTypeSlug', slug)}
            />
          </div>
        </div>

        {/* Banner — full-width, flush to edges */}
        <div className="mt-2 sm:mt-6 sm:max-w-[1280px] sm:mx-auto sm:px-6 lg:px-8">
          <CatalogBannerCarousel />
        </div>

        {/* Bundles — Paket Lengkap, prominent combo section */}
        {showBundlesSection && (
          <div id="paket-lengkap" className="mt-0 sm:mt-6">
            <div className={`${PAGE_CONTAINER} py-4 sm:py-8`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Layers className="w-5 h-5 text-teal-600" />
                <h2 className="text-lg sm:text-xl font-bold text-slate-900">{tBundles('title')}</h2>
              </div>
              <p className="text-sm text-slate-500 mb-4">{tBundles('subtitle')}</p>

              {/* Escape hatch — segment de-noising never hard-locks the bundle list */}
              {!policyPending && isBundlePersonalized && bundles.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-5">
                  <SlidersHorizontal className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                  <span>{showAllBundles ? tBundles('showingAll') : tBundles('showingPersonalized')}</span>
                  <button
                    type="button"
                    onClick={() => setShowAllBundles((v) => !v)}
                    aria-pressed={showAllBundles}
                    className="font-semibold text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    {showAllBundles ? tBundles('personalizeAgain') : tBundles('showAllToggle')}
                  </button>
                </div>
              )}

              {policyPending && isBundlePersonalized ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => <ModuleSkeleton key={i} />)}
                </div>
              ) : bundlesPersonalizedEmpty ? (
                <CatalogEmptyState
                  hasFilters
                  onReset={() => setShowAllBundles(true)}
                  itemLabel={tCatalog('paketLengkapLabel')}
                  allHref="/tests"
                />
              ) : (
                <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 scrollbar-hide sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible sm:pb-0">
                  {displayedBundles.map((b, i) => (
                    <div key={b.id} className="flex flex-shrink-0 w-[70vw] snap-start sm:w-auto">
                      <BundleCard bundle={b} priority={i === 0} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* No empty state for zero bundles on purpose. Paket Lengkap is a
            supplementary cross-sell, not what the page is for — when there are
            none the section simply doesn't render. An empty state here also
            mislabels a failed bundles fetch (which sets bundles to []) as
            "belum ada paket", and stacked a second placeholder above the
            modules one. The section header above already gates on
            `showBundlesSection`. */}

        <div className={`${PAGE_CONTAINER} pt-4 sm:pt-0`}>
          {/* Toolbar — Filter button + sort, all breakpoints. Exam family lives in the tiles under the title. */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setShowFilterPanel(v => !v)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                showFilterPanel ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {t('filter')}
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 bg-teal-500 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <div className="relative ml-auto flex-shrink-0">
              <select
                value={sort}
                onChange={e => updateParam('sort', e.target.value === 'newest' ? null : e.target.value)}
                className="appearance-none pl-3 pr-7 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-teal-300 transition-colors cursor-pointer focus:outline-none focus:border-teal-400"
              >
                <option value="newest">{t('sortTerbaru')}</option>
                <option value="price_asc">{t('sortHargaAsc')}</option>
                <option value="price_desc">{t('sortHargaDesc')}</option>
                <option value="discount">{t('sortDiskon')}</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Filter panel — Tipe / Penawaran / Durasi. Exam type is intentionally
              excluded (showExamTypeGroup=false): the tiles under the title already set it. */}
          {showFilterPanel && (
            <div className="mb-4 max-w-xs sm:max-w-sm">
              <Suspense>
                <CatalogSidebar examTypes={examTypes} countLabel="modules" showExamTypeGroup={false} sticky={false} />
              </Suspense>
            </div>
          )}

          {/* Full-width grid — reclaimed from the old 3-column layout */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => <ModuleSkeleton key={i} />)}
            </div>
          ) : sorted.length === 0 ? (
            <CatalogEmptyState
              hasFilters={hasFilters}
              onReset={() => router.replace(pathname, { scroll: false })}
              itemLabel="latihan soal"
              allHref="/tests"
            />
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-3">
                {t('menampilkan', {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, sorted.length),
                  total: sorted.length,
                })}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {paginated.map((m, i) => (
                  <ModuleCard key={m.id} module={m} showExamType={!examTypeSlug} priority={page === 1 && i === 0} />
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination page={page} totalPages={totalPages} onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              )}
            </>
          )}

          {/* Closing band — full-width at all breakpoints (was a `hidden xl:block` side rail) */}
          <div className="mt-10">
            <CatalogRightPanel type="tests" layout="horizontal" />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default function TestsCatalogClient({
  initialModules = [],
  initialBundles = [],
  initialExamTypes = [],
}: {
  initialModules?: TestModule[];
  initialBundles?: Bundle[];
  initialExamTypes?: ExamType[];
}) {
  return <ModuleCatalogContent initialModules={initialModules} initialBundles={initialBundles} initialExamTypes={initialExamTypes} />;
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  const t = useTranslations('testsCatalog');
  const pages = buildPageRange(page, totalPages);
  return (
    <div className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">{t('sebelumnya')}</span>
      </button>

      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p as number)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-teal-600 text-white border border-teal-600'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline">{t('berikutnya')}</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function buildPageRange(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}
